import { useState, useCallback, type ReactNode, type CSSProperties } from 'react';
import { cn } from '@/lib/utils';
import type { ModuleKey } from '@/lib/modules';

export interface SidebarItem {
  id: string;
  label: string;
  icon?: ReactNode;
  href?: string;
  badge?: string | number;
  children?: SidebarItem[];
  disabled?: boolean;
  permission?: string;
  /** User needs at least one of these permissions (when `permission` is not set). */
  anyPermissions?: string[];
  /**
   * Toggleable module this item belongs to. When set, the item is hidden if the
   * tenant's loaded `enabledModules` list omits the key (display/UX only).
   */
  module?: ModuleKey;
}

export interface SidebarProps {
  items: SidebarItem[];
  activeItemId?: string;
  onItemClick?: (item: SidebarItem) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  header?: ReactNode;
  footer?: ReactNode;
  className?: string;
  /** Inline style for the sidebar container (e.g. a brand gradient wash). */
  style?: CSSProperties;
  /** Inline style for the header slot (e.g. a stronger brand gradient). */
  headerStyle?: CSSProperties;
}

function SidebarNavItem({
  item,
  isActive,
  collapsed,
  depth = 0,
  onItemClick,
}: {
  item: SidebarItem;
  isActive: boolean;
  collapsed: boolean;
  depth?: number;
  onItemClick?: (item: SidebarItem) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = item.children && item.children.length > 0;

  const handleClick = useCallback(() => {
    if (hasChildren) {
      setExpanded((prev) => !prev);
    }
    onItemClick?.(item);
  }, [hasChildren, item, onItemClick]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  return (
    <li>
      <button
        type="button"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={item.disabled}
        data-testid={`nav-${item.id}`}
        aria-expanded={hasChildren ? expanded : undefined}
        aria-current={isActive ? 'page' : undefined}
        title={collapsed ? item.label : undefined}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
          'disabled:cursor-not-allowed disabled:opacity-50',
          depth > 0 && 'ml-4',
          isActive
            ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-300'
            : 'text-gray-700 hover:bg-surface-tertiary dark:text-gray-300 dark:hover:bg-surface-dark-tertiary',
        )}
      >
        {item.icon && (
          <span className="flex-shrink-0 h-5 w-5" aria-hidden="true">
            {item.icon}
          </span>
        )}
        {!collapsed && (
          <>
            <span className="flex-1 truncate text-left">{item.label}</span>
            {item.badge !== undefined && (
              <span className="ml-auto inline-flex items-center rounded-full bg-brand-100 px-2 py-0.5 text-2xs font-medium text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">
                {item.badge}
              </span>
            )}
            {hasChildren && (
              <svg
                className={cn('h-4 w-4 transition-transform', expanded && 'rotate-90')}
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </>
        )}
      </button>
      {hasChildren && expanded && !collapsed && (
        <ul role="group" className="mt-1 space-y-1">
          {item.children!.map((child) => (
            <SidebarNavItem
              key={child.id}
              item={child}
              isActive={false}
              collapsed={collapsed}
              depth={depth + 1}
              onItemClick={onItemClick}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export function Sidebar({
  items,
  activeItemId,
  onItemClick,
  collapsed = false,
  onToggleCollapse,
  header,
  footer,
  className,
  style,
  headerStyle,
}: SidebarProps) {
  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-border bg-white transition-all duration-200',
        'dark:border-border-dark dark:bg-surface-dark',
        collapsed ? 'w-16' : 'w-64',
        className,
      )}
      style={style}
      aria-label="Main navigation"
      data-testid="sidebar"
    >
      {/* Header */}
      {header && (
        <div
          className="flex h-16 items-center border-b border-border px-4 dark:border-border-dark"
          style={headerStyle}
        >
          {header}
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <ul role="list" className="space-y-1">
          {items.map((item) => (
            <SidebarNavItem
              key={item.id}
              item={item}
              isActive={item.id === activeItemId}
              collapsed={collapsed}
              onItemClick={onItemClick}
            />
          ))}
        </ul>
      </nav>

      {/* Collapse toggle */}
      {onToggleCollapse && (
        <div className="border-t border-border px-3 py-2 dark:border-border-dark">
          <button
            type="button"
            onClick={onToggleCollapse}
            className="flex w-full items-center justify-center rounded-lg p-2 text-gray-400 hover:bg-surface-tertiary hover:text-gray-600 dark:hover:bg-surface-dark-tertiary dark:hover:text-gray-300"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg
              className={cn('h-5 w-5 transition-transform', collapsed && 'rotate-180')}
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Footer */}
      {footer && (
        <div className="border-t border-border px-4 py-3 dark:border-border-dark">{footer}</div>
      )}
    </aside>
  );
}
